import { Stack } from "@fluentui/react";
import { logger, themeUtils, useTheme } from "cayo.ui";
import DOMPurify from "dompurify";
import { CSSProperties, FC, useCallback, useEffect, useMemo, useState } from "react";
import { IImage } from "../../api/schema.api";
import Icon from "./Icon";

const log = logger.getLogger("Image");

const sanitizeBase64SVG = (base64String: string): string => {
  const svgString = atob(base64String.split(",")[1]);

  const sanitizedSVG = DOMPurify.sanitize(svgString, {
    USE_PROFILES: { svg: true },
  });

  const sanitizedBase64 = `data:image/svg+xml;base64,${btoa(sanitizedSVG)}`;

  return sanitizedBase64;
};

const Image: FC<IImage> = (props) => {
  const { cayoTheme } = useTheme();
  const { width, height, url, fallbackIcon } = props;
  const [base64Image, setBase64Image] = useState<string>();

  const sanitizeImage = useCallback((base64String: string): string => {
    if (base64String.startsWith("data:image/svg+xml;base64")) {
      return sanitizeBase64SVG(base64String);
    }

    return base64String;
  }, []);

  useEffect(() => {
    const loadAsync = async () => {
      try {
        const imageData = await fetch(url!);
        const reader = new FileReader();
        reader.onload = (event: any) => {
          const result = sanitizeImage(event.target.result);
          setBase64Image(result);
        };
        const blob = await imageData.blob();
        if (blob?.size) {
          reader.readAsDataURL(blob);
        } else {
          setBase64Image("");
        }
      } catch (e) {
        log.error("Error loading image", e);
        setBase64Image("");
      }
    };

    if (url) {
      loadAsync();
    }
  }, [url, sanitizeImage]);

  const additionalStyle = useMemo<Partial<CSSProperties>>(
    () => (props.roundBorder ? themeUtils.getRoundBorderStyle(cayoTheme.brandColors) : {}),
    [props.roundBorder]
  );

  if (fallbackIcon && !base64Image) {
    return (
      <Stack
        verticalAlign={"center"}
        horizontalAlign={"center"}
        styles={{ root: { padding: 10, width, height, ...(additionalStyle as {}) } }}
      >
        <Icon {...fallbackIcon} />
      </Stack>
    );
  }

  return (
    <img
      src={fallbackIcon ? base64Image : props.url}
      alt=""
      width={width}
      height={height}
      style={additionalStyle}
    />
  );
};

export default Image;
